import React, { useState, useEffect } from 'react';

const RenderProgressModal = ({ isVisible, progress, currentStep, onForceClose }) => {
  const [smoothProgress, setSmoothProgress] = useState(0);
  const [startTime, setStartTime] = useState(null);
  
  // Track start time and force close after 10 seconds
  useEffect(() => {
    if (isVisible && !startTime) {
      setStartTime(Date.now());
    } else if (!isVisible) {
      setStartTime(null);
    }
  }, [isVisible, startTime]);
  
  // Force close after 10 seconds if stuck
  useEffect(() => {
    if (!isVisible || !startTime) return;
    
    const timeout = setTimeout(() => {
      console.warn('⚠️ Progress modal stuck, force closing after 10 seconds');
      if (onForceClose) onForceClose();
    }, 10000);
    
    return () => clearTimeout(timeout);
  }, [isVisible, startTime, onForceClose]);
  
  // ESC key to force close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVisible && onForceClose) {
        console.log('⚠️ Progress modal closed by ESC key');
        onForceClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isVisible, onForceClose]);
  
  // Smooth animation - ramps up continuously
  useEffect(() => {
    if (!isVisible) {
      setSmoothProgress(0);
      return;
    }
    
    // Target progress based on step
    const targetProgress = {
      'capture': 25,
      'gps': 50,
      'businesses': 85,
      'render': 100
    }[currentStep] || 0;
    
    // Smoothly animate to target
    const interval = setInterval(() => {
      setSmoothProgress(prev => {
        if (prev >= targetProgress) return prev;
        
        // Smooth increment - faster at start, slower near target
        const diff = targetProgress - prev;
        const increment = Math.max(0.5, diff / 10);
        
        return Math.min(targetProgress, prev + increment);
      });
    }, 30); // Update every 30ms for smooth animation
    
    return () => clearInterval(interval);
  }, [isVisible, currentStep]);
  
  if (!isVisible) return null;

  const progressPercent = Math.round(smoothProgress);

  return (
    <div className="fixed inset-0 bg-[var(--pbfs-navy)] z-[99999] flex items-center justify-center p-4">
      <div className="flex flex-col items-center space-y-8 max-w-sm w-full">
        
        {/* PBFS Logo */}
        <img 
          src="/images/PBFS-white.png" 
          alt="PBFS"
          className="w-32 h-auto"
        />
        
        {/* Smooth Progress Bar */}
        <div className="w-full space-y-3">
          {/* Progress Bar */}
          <div className="w-full h-2.5 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[var(--pbfs-green)] rounded-full shadow-lg shadow-[var(--pbfs-green)]/50 transition-all duration-100 ease-linear"
              style={{ width: `${smoothProgress}%` }}
            />
          </div>
          
          {/* Main Text - Always "Rendering" */}
          <div className="text-white text-lg text-center font-medium tracking-wide">
            Rendering
          </div>
          
          {/* Percentage - Smaller */}
          <div className="text-white/60 text-sm text-center">
            {progressPercent}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenderProgressModal;

